/**
 * Kept Below: product page behaviour (loaded only by sections/main-product.liquid).
 *
 * <product-info>   variant picker + add to cart (announces changes, never adds a stale variant)
 * <media-gallery>  thumbnails, scroll-snap slides, follows the selected variant's image
 * <media-lightbox> full-screen image viewer in a native <dialog>
 * <sticky-atc>     compact add-to-cart bar once the main button scrolls away (small screens)
 */

const { routes, prefersReducedMotion, lockScroll, announce, define } = window.KeptBelow;

/**
 * @typedef {object} Variant
 * @property {number} id
 * @property {string[]} options
 * @property {boolean} available
 * @property {string} price   formatted, plain text
 * @property {string} compare formatted, plain text
 * @property {boolean} on_sale
 * @property {number | null} media
 */

/** @param {Element} element */
const sectionOf = (element) => element.closest('.shopify-section') ?? document;

class ProductInfo extends HTMLElement {
  #controller = new AbortController();
  /** @type {Variant[]} */
  #variants = [];
  #inFlight = false;
  /** @type {HTMLFormElement | null} */
  #form = null;

  connectedCallback() {
    this.#controller = new AbortController();
    const { signal } = this.#controller;
    this.#form = this.querySelector('form[data-product-add]');

    const data = this.querySelector('[data-variants]');
    if (data?.textContent) {
      try {
        this.#variants = JSON.parse(data.textContent);
      } catch (error) {
        console.error('[Kept Below] could not read product variants', error);
      }
    }

    this.addEventListener(
      'change',
      (event) => {
        if (/** @type {Element} */ (event.target).closest('[data-option]')) this.#onOptionChange();
      },
      { signal },
    );
    this.#form?.addEventListener('submit', (event) => this.#onSubmit(event), { signal });
    if (this.#variants.length) this.#markAvailability(this.#selectedOptions());
  }

  disconnectedCallback() {
    this.#controller.abort();
  }

  /** The main button plus any button elsewhere that submits this form (the sticky bar). */
  get #buttons() {
    if (!this.#form) return [];
    const main = this.#form.querySelector('[data-add-to-cart]');
    const external = this.#form.id ? [...document.querySelectorAll(`button[form="${CSS.escape(this.#form.id)}"]`)] : [];
    return /** @type {HTMLButtonElement[]} */ ([main, ...external].filter(Boolean));
  }

  /** @returns {(string | undefined)[]} */
  #selectedOptions() {
    return [...this.querySelectorAll('[data-option]')].map(
      (fieldset) => /** @type {HTMLInputElement | null} */ (fieldset.querySelector('input:checked'))?.value,
    );
  }

  /** @param {(string | undefined)[]} options */
  #findVariant(options) {
    return this.#variants.find((variant) => variant.options.every((value, index) => value === options[index]));
  }

  #onOptionChange() {
    const current = this.#selectedOptions();
    const variant = this.#findVariant(current);
    this.#markAvailability(current);

    const idInput = /** @type {HTMLInputElement | null} */ (this.#form?.querySelector('[name="id"]'));
    // Clearing the id means an impossible combination can never add the previously selected variant.
    if (idInput) idInput.value = variant ? String(variant.id) : '';

    if (!variant) {
      this.#setButtons(false, this.dataset.labelUnavailable);
      announce(this.dataset.labelUnavailable || '');
      return;
    }

    this.#setButtons(variant.available, variant.available ? this.dataset.labelAdd : this.dataset.labelSoldOut);
    this.#renderPrice(variant);
    announce(variant.available ? variant.price : `${variant.price}, ${this.dataset.labelSoldOut}`);

    const url = new URL(window.location.href);
    url.searchParams.set('variant', String(variant.id));
    window.history.replaceState(window.history.state, '', url);
    this.dispatchEvent(new CustomEvent('variant:change', { bubbles: true, detail: { variant } }));
  }

  /** @param {(string | undefined)[]} current */
  #markAvailability(current) {
    const fieldsets = this.querySelectorAll('[data-option]');
    fieldsets.forEach((fieldset, position) => {
      for (const input of /** @type {NodeListOf<HTMLInputElement>} */ (fieldset.querySelectorAll('input'))) {
        const combination = [...current];
        combination[position] = input.value;
        const match = this.#findVariant(combination);
        const unavailable = !match || !match.available;
        input.classList.toggle('is-unavailable', unavailable);
        if (unavailable) input.setAttribute('aria-label', `${input.value}, ${this.dataset.labelItemUnavailable}`);
        else input.removeAttribute('aria-label');
      }
      const label = fieldset.querySelector('legend span');
      if (label) label.textContent = current[position] || '';
    });
  }

  /**
   * @param {boolean} enabled
   * @param {string} [label]
   */
  #setButtons(enabled, label = '') {
    for (const button of this.#buttons) {
      button.disabled = !enabled;
      (button.querySelector('span') ?? button).textContent = label;
    }
  }

  /** Renders the same markup as snippets/price.liquid, with text only (no HTML injection). */
  /** @param {Variant} variant */
  #renderPrice(variant) {
    for (const wrapper of sectionOf(this).querySelectorAll('[data-price-wrapper]')) {
      const price = document.createElement('span');
      price.className = 'price';
      price.dataset.price = '';
      /**
       * @param {string} className
       * @param {string} text
       * @param {string} [tag]
       */
      const add = (className, text, tag = 'span') => {
        const element = document.createElement(tag);
        element.className = className;
        element.textContent = text;
        price.append(element);
      };
      if (variant.on_sale) {
        add('visually-hidden', this.dataset.labelSale || '');
        add('price__current', variant.price);
        add('visually-hidden', this.dataset.labelRegular || '');
        add('price__compare', variant.compare, 's');
      } else {
        add('price__current', variant.price);
      }
      wrapper.replaceChildren(price);
    }
  }

  /** @param {SubmitEvent} event */
  async #onSubmit(event) {
    event.preventDefault();
    const form = this.#form;
    const idInput = /** @type {HTMLInputElement | null} */ (form?.querySelector('[name="id"]'));
    if (!form || this.#inFlight || !idInput?.value) return;

    const buttons = this.#buttons;
    const wasDisabled = buttons.map((button) => button.disabled);
    const error = /** @type {HTMLElement | null} */ (form.querySelector('[data-form-error]'));
    const trigger = /** @type {HTMLElement | null} */ (event.submitter) ?? buttons[0] ?? null;
    if (error) error.hidden = true;

    this.#inFlight = true;
    for (const button of buttons) {
      button.disabled = true;
      button.setAttribute('aria-busy', 'true');
    }

    const body = new FormData(form);
    body.append('sections', 'cart-drawer');
    body.append('sections_url', window.location.pathname);

    /** @type {Response | undefined} */
    let response;
    let added = null;
    try {
      response = await fetch(routes.cartAdd, {
        method: 'POST',
        headers: { Accept: 'application/javascript', 'X-Requested-With': 'XMLHttpRequest' },
        body,
      });
      const json = await response.json();
      if (!response.ok || json.status) throw new Error(json.description || json.message || '');
      added = json;
    } catch (failure) {
      if (!response && failure instanceof TypeError) {
        // Network failure before any response: fall back to a normal form post.
        HTMLFormElement.prototype.submit.call(form);
        return;
      }
      if (error) {
        error.textContent = (failure instanceof Error && failure.message) || this.dataset.labelError || '';
        error.hidden = false;
      }
    } finally {
      buttons.forEach((button, index) => {
        button.disabled = wasDisabled[index];
        button.removeAttribute('aria-busy');
      });
      this.#inFlight = false;
    }

    if (!added) return;
    announce((this.dataset.labelAdded || '').replace('__TITLE__', added.product_title || ''));
    if (!document.querySelector('cart-drawer')) {
      window.location.assign(routes.cart || '/cart');
      return;
    }
    document.dispatchEvent(new CustomEvent('cart:updated', { detail: { sections: added.sections, open: true, trigger } }));
  }
}

class MediaGallery extends HTMLElement {
  #controller = new AbortController();
  /** @type {IntersectionObserver | null} */
  #observer = null;

  connectedCallback() {
    this.#controller = new AbortController();
    const { signal } = this.#controller;
    const track = this.querySelector('.gallery__track');
    if (!track) return;
    const slides = [...track.children];
    const thumbs = [...this.querySelectorAll('.gallery__thumb')];
    const dots = [...this.querySelectorAll('.gallery__dots span')];

    /** @param {number} index */
    const setActive = (index) => {
      thumbs.forEach((thumb, i) => thumb.setAttribute('aria-current', String(i === index)));
      dots.forEach((dot, i) => dot.classList.toggle('is-active', i === index));
    };
    /** @param {number} index */
    this.goTo = (index) => {
      const slide = /** @type {HTMLElement | undefined} */ (slides[index]);
      if (!slide) return;
      // Scroll the track only; scrollIntoView could also jump the page vertically.
      track.scrollTo({ left: slide.offsetLeft - /** @type {HTMLElement} */ (track).offsetLeft, behavior: prefersReducedMotion() ? 'auto' : 'smooth' });
      setActive(index);
    };

    thumbs.forEach((thumb, index) => thumb.addEventListener('click', () => this.goTo?.(index), { signal }));

    this.#observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) if (entry.isIntersecting) setActive(slides.indexOf(entry.target));
      },
      { root: track, threshold: 0.6 },
    );
    for (const slide of slides) this.#observer.observe(slide);

    this.addEventListener(
      'click',
      (event) => {
        const opener = /** @type {HTMLElement | null} */ (/** @type {Element} */ (event.target).closest('[data-lightbox-open]'));
        if (!opener) return;
        this.dispatchEvent(
          new CustomEvent('lightbox:open', { bubbles: true, detail: { index: Number(opener.dataset.lightboxOpen), trigger: opener } }),
        );
      },
      { signal },
    );

    sectionOf(this).addEventListener(
      'variant:change',
      (event) => {
        const mediaId = /** @type {CustomEvent} */ (event).detail?.variant?.media;
        const index = slides.findIndex((slide) => /** @type {HTMLElement} */ (slide).dataset.mediaId === String(mediaId));
        if (index > -1) this.goTo?.(index);
      },
      { signal },
    );
  }

  disconnectedCallback() {
    this.#controller.abort();
    this.#observer?.disconnect();
  }
}

class MediaLightbox extends HTMLElement {
  #controller = new AbortController();
  /** @type {IntersectionObserver | null} */
  #observer = null;
  #index = 0;
  #release = () => {};
  /** @type {HTMLElement | null} */
  #trigger = null;
  #frame = 0;

  connectedCallback() {
    this.#controller = new AbortController();
    const { signal } = this.#controller;
    const dialog = this.querySelector('dialog');
    const track = this.querySelector('[data-lightbox-track]');
    if (!dialog || !track) return;
    const slides = /** @type {HTMLElement[]} */ ([...track.children]);
    const counter = this.querySelector('[data-lightbox-counter]');

    /** @param {number} index */
    const setIndex = (index) => {
      this.#index = index;
      if (counter) counter.textContent = `${index + 1} / ${slides.length}`;
    };
    const unzoom = () => track.querySelectorAll('img.is-zoomed').forEach((img) => img.classList.remove('is-zoomed'));
    /** Loads only the requested image and its neighbours, not the whole gallery. */
    /** @param {number} index */
    const loadAround = (index) => {
      for (const offset of [-1, 0, 1]) {
        const img = slides[(index + offset + slides.length) % slides.length]?.querySelector('img');
        if (img) img.loading = 'eager';
      }
    };
    /**
     * @param {number} index
     * @param {boolean} [smooth]
     */
    const go = (index, smooth = true) => {
      const next = (index + slides.length) % slides.length;
      unzoom();
      loadAround(next);
      track.scrollTo({ left: slides[next].offsetLeft, behavior: smooth && !prefersReducedMotion() ? 'smooth' : 'instant' });
      setIndex(next);
    };

    sectionOf(this).addEventListener(
      'lightbox:open',
      (event) => {
        const { index, trigger } = /** @type {CustomEvent} */ (event).detail;
        this.#trigger = trigger;
        loadAround(index);
        dialog.showModal();
        this.#release = lockScroll();
        requestAnimationFrame(() => go(index, false));
      },
      { signal },
    );
    dialog.addEventListener(
      'close',
      () => {
        unzoom();
        this.#release();
        this.#release = () => {};
        if (this.#trigger?.isConnected) this.#trigger.focus();
      },
      { signal },
    );
    this.querySelector('[data-lightbox-close]')?.addEventListener('click', () => dialog.close(), { signal });
    this.querySelector('[data-lightbox-prev]')?.addEventListener('click', () => go(this.#index - 1), { signal });
    this.querySelector('[data-lightbox-next]')?.addEventListener('click', () => go(this.#index + 1), { signal });
    dialog.addEventListener(
      'keydown',
      (event) => {
        if (event.key !== 'ArrowRight' && event.key !== 'ArrowLeft') return;
        event.preventDefault();
        go(this.#index + (event.key === 'ArrowRight' ? 1 : -1));
      },
      { signal },
    );

    this.#observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) if (entry.isIntersecting) setIndex(slides.indexOf(/** @type {HTMLElement} */ (entry.target)));
      },
      { root: track, threshold: 0.6 },
    );
    for (const slide of slides) this.#observer.observe(slide);

    /**
     * @param {HTMLImageElement} img
     * @param {MouseEvent} event
     */
    const setOrigin = (img, event) => {
      const rect = /** @type {HTMLElement} */ (img.parentElement).getBoundingClientRect();
      img.style.transformOrigin = `${((event.clientX - rect.left) / rect.width) * 100}% ${((event.clientY - rect.top) / rect.height) * 100}%`;
    };
    track.addEventListener(
      'click',
      (event) => {
        const img = /** @type {Element} */ (event.target).closest('img');
        if (!img || window.matchMedia('(hover: none)').matches) return;
        setOrigin(img, /** @type {MouseEvent} */ (event));
        img.classList.toggle('is-zoomed');
      },
      { signal },
    );
    track.addEventListener(
      'mousemove',
      (event) => {
        const img = /** @type {Element} */ (event.target).closest('img.is-zoomed');
        if (!img || this.#frame) return;
        this.#frame = requestAnimationFrame(() => {
          this.#frame = 0;
          setOrigin(/** @type {HTMLImageElement} */ (img), /** @type {MouseEvent} */ (event));
        });
      },
      { signal },
    );
  }

  disconnectedCallback() {
    this.#controller.abort();
    this.#observer?.disconnect();
    this.#release();
  }
}

class StickyAtc extends HTMLElement {
  #controller = new AbortController();
  /** @type {IntersectionObserver | null} */
  #observer = null;
  #frame = 0;

  connectedCallback() {
    this.#controller = new AbortController();
    const buyForm = sectionOf(this).querySelector('.product .buy');
    if (!buyForm) return;
    /** Shown once the main buy button has scrolled up out of view. */
    const update = () => {
      const show = buyForm.getBoundingClientRect().bottom < 0;
      this.classList.toggle('is-visible', show);
      this.setAttribute('aria-hidden', String(!show));
      this.inert = !show;
    };
    this.#observer = new IntersectionObserver(update);
    this.#observer.observe(buyForm);
    // Backup for engines that deliver observer callbacks late; one read per frame at most.
    window.addEventListener(
      'scroll',
      () => {
        if (this.#frame) return;
        this.#frame = requestAnimationFrame(() => {
          this.#frame = 0;
          update();
        });
      },
      { passive: true, signal: this.#controller.signal },
    );
  }

  disconnectedCallback() {
    this.#controller.abort();
    this.#observer?.disconnect();
    cancelAnimationFrame(this.#frame);
  }
}

define('product-info', ProductInfo);
define('media-gallery', MediaGallery);
define('media-lightbox', MediaLightbox);
define('sticky-atc', StickyAtc);

export {};
